import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

/** A4 at ~96dpi — keep in sync with TinyMCE page layout */
export const PDF_PAGE = {
  widthPx: 794,
  heightPx: 1123,
  /** @deprecated use DEFAULT_PAGE_MARGINS_MM */
  marginPx: 56,
};

/** Default page margins in millimeters (Word-like). */
export const DEFAULT_PAGE_MARGINS_MM = {
  top: 20,
  right: 20,
  bottom: 20,
  left: 20,
};

/**
 * @param {unknown} input
 * @returns {{ top: number, right: number, bottom: number, left: number }}
 */
export function normalizePageMargins(input) {
  const src = input && typeof input === 'object' ? /** @type {Record<string, unknown>} */ (input) : {};
  const clamp = (value, fallback) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return fallback;
    return Math.min(80, Math.max(0, Math.round(n * 10) / 10));
  };
  return {
    top: clamp(src.top, DEFAULT_PAGE_MARGINS_MM.top),
    right: clamp(src.right, DEFAULT_PAGE_MARGINS_MM.right),
    bottom: clamp(src.bottom, DEFAULT_PAGE_MARGINS_MM.bottom),
    left: clamp(src.left, DEFAULT_PAGE_MARGINS_MM.left),
  };
}

/**
 * @param {number} mm
 */
export function mmToPx(mm) {
  return (Number(mm) * 96) / 25.4;
}

/**
 * @param {unknown} marginsMm
 */
export function resolvePageBox(marginsMm) {
  const margins = normalizePageMargins(marginsMm);
  const top = Math.max(0, Math.round(mmToPx(margins.top)));
  const right = Math.max(0, Math.round(mmToPx(margins.right)));
  const bottom = Math.max(0, Math.round(mmToPx(margins.bottom)));
  const left = Math.max(0, Math.round(mmToPx(margins.left)));
  const contentWidth = Math.max(120, PDF_PAGE.widthPx - left - right);
  const contentHeight = Math.max(120, PDF_PAGE.heightPx - top - bottom);
  return {
    marginsMm: margins,
    top,
    right,
    bottom,
    left,
    contentWidth,
    contentHeight,
    pageWidth: PDF_PAGE.widthPx,
    pageHeight: PDF_PAGE.heightPx,
  };
}

const DOCUMENT_CSS = `
  .pdf-export-measure {
    font-family: Calibri, Candara, Segoe, "Segoe UI", Optima, Arial, Helvetica, sans-serif;
    font-size: 11pt;
    line-height: 1.5;
    color: #111827;
  }
  .pdf-export-measure h1 { font-size: 24pt; line-height: 1.25; margin: 0 0 14pt; font-weight: 700; }
  .pdf-export-measure h2 { font-size: 16pt; line-height: 1.3; margin: 18pt 0 10pt; font-weight: 700; }
  .pdf-export-measure h3 { font-size: 13pt; line-height: 1.35; margin: 14pt 0 8pt; font-weight: 700; }
  .pdf-export-measure h4 { font-size: 12pt; margin: 12pt 0 6pt; font-weight: 700; }
  .pdf-export-measure p { margin: 0 0 10pt; }
  .pdf-export-measure ul, .pdf-export-measure ol { margin: 0 0 10pt; padding-left: 24pt; }
  .pdf-export-measure li { margin: 0 0 4pt; }
  .pdf-export-measure img { max-width: 100%; height: auto; display: inline-block; vertical-align: top; }
  .pdf-export-measure table { width: 100%; max-width: 100%; border-collapse: collapse; margin: 12pt 0; }
  .pdf-export-measure td, .pdf-export-measure th { border: 1px solid #9ca3af; padding: 6pt 8pt; vertical-align: top; text-align: left; }
  .pdf-export-measure .pdf-import-page-image { margin: 0; text-align: center; }
  .pdf-export-measure blockquote {
    margin: 12pt 0; padding: 6pt 14pt; border-left: 3px solid #9ca3af; color: #374151;
  }
  .pdf-export-measure a { color: #1d4ed8; }
  .pdf-export-measure hr { border: 0; border-top: 1px solid #cbd5e1; margin: 18pt 0; }
  .pdf-export-measure .mce-pagebreak,
  .pdf-export-measure .pdf-manual-page-break,
  .pdf-export-measure [data-pdf-auto-break],
  .pdf-export-measure [data-pdf-page-gap] {
    display: block;
    height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    border: 0 !important;
    overflow: hidden !important;
    page-break-before: always;
  }
  .pdf-export-measure [data-pdf-page],
  .pdf-export-measure .pdf-doc-page {
    display: contents;
  }
`;

/**
 * Render HTML document content into a multi-page PDF blob (A4).
 * Uses a continuous render + page slices with margins so text is not clipped mid-line.
 * @param {string} html
 * @param {{ title?: string, margins?: { top?: number, right?: number, bottom?: number, left?: number } }} [opts]
 * @returns {Promise<Blob>}
 */
export async function htmlDocumentToPdfBlob(html, opts = {}) {
  const box = resolvePageBox(opts.margins);
  const scale = 2;

  const host = document.createElement('div');
  host.style.cssText = [
    'position:fixed',
    'left:0',
    'top:0',
    'opacity:0',
    'pointer-events:none',
    'z-index:-1',
    `width:${box.contentWidth}px`,
  ].join(';');

  const style = document.createElement('style');
  style.textContent = DOCUMENT_CSS;
  host.appendChild(style);

  const measure = document.createElement('div');
  measure.className = 'pdf-export-measure';
  measure.style.cssText = [
    `width:${box.contentWidth}px`,
    'box-sizing:border-box',
    'background:#ffffff',
    'color:#111827',
  ].join(';');
  measure.innerHTML = sanitizeExportHtml(html || '<p></p>');
  host.appendChild(measure);
  document.body.appendChild(host);

  try {
    if (document.fonts?.ready) {
      await document.fonts.ready.catch(() => {});
    }
    await waitForImages(measure);
    // Give layout a tick after images get intrinsic sizes.
    await new Promise((r) => window.requestAnimationFrame(() => r(null)));
    void measure.offsetHeight;

    const canvas = await html2canvas(measure, {
      scale,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false,
      width: box.contentWidth,
      windowWidth: box.contentWidth,
      imageTimeout: 15000,
      onclone: (clonedDoc) => {
        clonedDoc.querySelectorAll('img').forEach((img) => {
          img.style.maxWidth = '100%';
          img.style.height = 'auto';
          img.style.display = 'inline-block';
        });
        clonedDoc.querySelectorAll('table').forEach((table) => {
          table.style.width = '100%';
          table.style.maxWidth = '100%';
          table.style.borderCollapse = 'collapse';
        });
      },
    });

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    const pageCanvasW = box.pageWidth * scale;
    const pageCanvasH = box.pageHeight * scale;
    const marginTopPx = box.top * scale;
    const marginLeftPx = box.left * scale;
    const contentHPx = box.contentHeight * scale;

    // Forced page breaks from TinyMCE — split source canvas at marker positions when present.
    const breakYs = findForcedBreakYs(measure, canvas, scale);
    const segments = buildSegments(canvas.height, contentHPx, breakYs);

    let pageIndex = 0;
    for (const segment of segments) {
      let y = segment.start;
      while (y < segment.end - 1) {
        let sliceH = Math.min(contentHPx, segment.end - y);
        if (y + sliceH < segment.end) {
          sliceH = snapToQuietRow(canvas, y, sliceH);
        }
        sliceH = Math.max(1, Math.floor(sliceH));

        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = pageCanvasW;
        pageCanvas.height = pageCanvasH;
        const ctx = pageCanvas.getContext('2d');
        if (!ctx) break;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, pageCanvasW, pageCanvasH);
        ctx.drawImage(
          canvas,
          0,
          y,
          canvas.width,
          sliceH,
          marginLeftPx,
          marginTopPx,
          canvas.width,
          sliceH,
        );

        const data = pageCanvas.toDataURL('image/jpeg', 0.93);
        if (pageIndex > 0) pdf.addPage();
        pdf.addImage(data, 'JPEG', 0, 0, pdfWidth, pdfHeight);

        y += sliceH;
        pageIndex += 1;
        if (pageIndex > 40) break;
      }
      if (pageIndex > 40) break;
    }

    if (pageIndex === 0) {
      // Empty document — still emit one blank page.
      pdf.rect(0, 0, pdfWidth, pdfHeight);
    }

    if (opts.title) {
      pdf.setProperties({ title: opts.title });
    }

    return pdf.output('blob');
  } finally {
    host.remove();
  }
}

/**
 * Remove editor-only chrome before PDF render.
 * @param {string} html
 */
function sanitizeExportHtml(html) {
  const wrap = document.createElement('div');
  wrap.innerHTML = html || '';
  wrap.querySelectorAll('[data-pdf-auto-break="1"]').forEach((n) => n.remove());
  wrap.querySelectorAll('[data-pdf-page-gap="1"], .pdf-doc-page-gap').forEach((n) => n.remove());
  // Unwrap editor page shells if they ever leak into stored HTML.
  wrap.querySelectorAll('[data-pdf-page], .pdf-doc-page').forEach((page) => {
    const parent = page.parentNode;
    if (!parent) return;
    while (page.firstChild) parent.insertBefore(page.firstChild, page);
    page.remove();
  });
  wrap.querySelectorAll('.mce-pagebreak, .pdf-manual-page-break, [data-mce-type="pagebreak"]').forEach((n) => {
    // Keep a zero-height marker so we can detect forced breaks by position.
    const marker = document.createElement('div');
    marker.className = 'mce-pagebreak';
    marker.setAttribute('data-mce-type', 'pagebreak');
    marker.style.cssText = 'display:block;height:0;margin:0;padding:0;border:0;overflow:hidden;';
    n.replaceWith(marker);
  });
  return wrap.innerHTML || '<p></p>';
}

/**
 * @param {HTMLElement} measure
 * @param {HTMLCanvasElement} canvas
 * @param {number} scale
 * @returns {number[]}
 */
function findForcedBreakYs(measure, canvas, scale) {
  const breaks = [];
  const measureTop = measure.getBoundingClientRect().top;
  measure.querySelectorAll('.mce-pagebreak, [data-mce-type="pagebreak"]').forEach((el) => {
    const y = Math.round((el.getBoundingClientRect().top - measureTop) * scale);
    if (y > 0 && y < canvas.height) breaks.push(y);
  });
  return breaks.sort((a, b) => a - b);
}

/**
 * @param {number} totalHeight
 * @param {number} contentHPx
 * @param {number[]} breakYs
 * @returns {{ start: number, end: number }[]}
 */
function buildSegments(totalHeight, contentHPx, breakYs) {
  const points = [0, ...breakYs.filter((y) => y > 0 && y < totalHeight), totalHeight];
  const unique = [...new Set(points)].sort((a, b) => a - b);
  const segments = [];
  for (let i = 0; i < unique.length - 1; i += 1) {
    const start = unique[i];
    const end = unique[i + 1];
    if (end - start > 1) segments.push({ start, end });
  }
  if (segments.length === 0) {
    segments.push({ start: 0, end: Math.max(totalHeight, contentHPx) });
  }
  return segments;
}

/**
 * Prefer slicing on a nearly blank row so glyphs are not cut in half.
 * @param {HTMLCanvasElement} canvas
 * @param {number} startY
 * @param {number} sliceH
 */
function snapToQuietRow(canvas, startY, sliceH) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return sliceH;
  const width = canvas.width;
  const minH = Math.max(24, Math.floor(sliceH * 0.65));
  const maxLook = Math.min(120, sliceH - minH);
  let best = sliceH;

  for (let dy = 0; dy < maxLook; dy += 1) {
    const row = Math.floor(startY + sliceH - 1 - dy);
    if (row <= startY) break;
    let ink = 0;
    const data = ctx.getImageData(0, row, width, 1).data;
    for (let i = 0; i < data.length; i += 16) {
      if (data[i] < 248 || data[i + 1] < 248 || data[i + 2] < 248) ink += 1;
    }
    const samples = Math.ceil(width / 4);
    if (ink < samples * 0.03) {
      best = sliceH - dy;
      break;
    }
  }
  return Math.max(minH, best);
}

/**
 * @param {HTMLElement} root
 */
function waitForImages(root) {
  const images = Array.from(root.querySelectorAll('img'));
  if (images.length === 0) return Promise.resolve();
  return Promise.all(
    images.map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete) {
            resolve();
            return;
          }
          img.onload = () => resolve();
          img.onerror = () => resolve();
        }),
    ),
  );
}

/**
 * @param {Record<string, any>} report
 * @param {{ url?: string, pagesScanned?: number }} meta
 */
export function reportToHtml(report, meta = {}) {
  const escape = (s) =>
    String(s || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

  const title = escape(report.title || 'Website improvement analysis');
  const summary = escape(report.executive_summary || '');
  const score = report.overall_score != null ? String(report.overall_score) : null;
  const parts = [];

  parts.push(`<h1>${title}</h1>`);
  parts.push('<p><em>');
  if (meta.url) parts.push(`Analyzed URL: ${escape(meta.url)}`);
  if (meta.pagesScanned != null) parts.push(`${meta.url ? ' · ' : ''}Pages scanned: ${meta.pagesScanned}`);
  if (score) parts.push(` · Overall score: ${escape(score)}/10`);
  parts.push('</em></p>');

  if (summary) {
    parts.push('<h2>Executive summary</h2>');
    parts.push(`<p>${summary.replace(/\n/g, '<br>')}</p>`);
  }

  const sections = Array.isArray(report.sections) ? report.sections : [];
  for (const section of sections) {
    parts.push(`<h2>${escape(section.heading || 'Findings')}</h2>`);
    if (section.findings) {
      parts.push(`<p>${escape(section.findings).replace(/\n/g, '<br>')}</p>`);
    }
    const recs = Array.isArray(section.recommendations) ? section.recommendations : [];
    if (recs.length) {
      parts.push('<ul>');
      for (const rec of recs) {
        if (String(rec || '').trim()) parts.push(`<li>${escape(rec)}</li>`);
      }
      parts.push('</ul>');
    }
  }

  const quickWins = Array.isArray(report.quick_wins) ? report.quick_wins : [];
  if (quickWins.length) {
    parts.push('<h2>Quick wins</h2><ul>');
    for (const item of quickWins) {
      if (String(item || '').trim()) parts.push(`<li>${escape(item)}</li>`);
    }
    parts.push('</ul>');
  }

  const roadmap = Array.isArray(report.priority_roadmap) ? report.priority_roadmap : [];
  if (roadmap.length) {
    parts.push('<h2>Priority roadmap</h2><ul>');
    for (const item of roadmap) {
      if (String(item || '').trim()) parts.push(`<li>${escape(item)}</li>`);
    }
    parts.push('</ul>');
  }

  return parts.join('\n');
}

export const BLANK_DOCUMENT_HTML = `
<h1>Untitled document</h1>
<p>Start writing your document here. Use the toolbar for headings, lists, tables, links, and images.</p>
`.trim();
